import { useEffect, useState } from 'react';
import { type ThemeKnobs, DEFAULT_KNOBS } from '@/lib/theme/theme-knobs';
import { parseKnobsFromCss, hasThemeKnobsMarker } from '@/lib/theme/theme-knobs-header';

/**
 * Where the seed knobs came from:
 * - `default`: no stylesheet on the service → start from defaults.
 * - `header`:  existing stylesheet carries a valid `@theme-knobs` header (round-trip).
 * - `custom`:  existing stylesheet has no header → hand-crafted CSS; warn before overwrite.
 * - `error`:   the stylesheet URL could not be fetched (CORS / network).
 */
export type KnobsSource = 'default' | 'header' | 'custom' | 'error';

export interface UseStylesheetKnobsResult {
  status: 'loading' | 'ready';
  /** Seed knobs to initialise the editor with. */
  knobs: ThemeKnobs;
  source: KnobsSource;
}

/**
 * Initial-load for the theme editor (Spec 340): fetch the service's current
 * stylesheet RAW (not via the sandboxing loader, which would strip the comment
 * header) and read its `@theme-knobs` header to re-open the last knob values.
 * Falls back to defaults on a missing/invalid header, and flags hand-crafted CSS.
 */
export function useStylesheetKnobs(stylesheetUrl?: string | null): UseStylesheetKnobsResult {
  const [result, setResult] = useState<UseStylesheetKnobsResult>(() =>
    stylesheetUrl
      ? { status: 'loading', knobs: DEFAULT_KNOBS, source: 'default' }
      : { status: 'ready', knobs: DEFAULT_KNOBS, source: 'default' },
  );

  useEffect(() => {
    if (!stylesheetUrl) {
      setResult({ status: 'ready', knobs: DEFAULT_KNOBS, source: 'default' });
      return;
    }

    let cancelled = false;
    setResult({ status: 'loading', knobs: DEFAULT_KNOBS, source: 'default' });

    fetch(stylesheetUrl, { method: 'GET' })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then((css) => {
        if (cancelled) return;
        const parsed = parseKnobsFromCss(css);
        if (parsed) {
          setResult({ status: 'ready', knobs: parsed, source: 'header' });
        } else if (hasThemeKnobsMarker(css)) {
          // Marker present but malformed/invalid → editor-authored; silently fall
          // back to defaults (no overwrite warning), per spec.
          setResult({ status: 'ready', knobs: DEFAULT_KNOBS, source: 'default' });
        } else {
          // No marker → hand-crafted custom CSS → warn before overwrite.
          setResult({ status: 'ready', knobs: DEFAULT_KNOBS, source: 'custom' });
        }
      })
      .catch(() => {
        if (!cancelled) setResult({ status: 'ready', knobs: DEFAULT_KNOBS, source: 'error' });
      });

    return () => {
      cancelled = true;
    };
  }, [stylesheetUrl]);

  return result;
}
